import { useCallback, useEffect, useState } from 'react';
import { fetchManufacturingEquipments } from '../../api/manufacturingEquipment';
import { useCompanyStore } from '../../store/companyStore';
import { EquipmentType, ManufacturingEquipment } from '../../types/manufacturingEquipment';
import EquipUseStatus from './EquipUseStatus';
import InspectionCalibOverview from './InspectionCalibOverview';
import ManufacturingEquipDetail from './ManufacturingEquipDetail';
import ManufacturingEquipForm from './ManufacturingEquipForm';
import { useEquipListSort, useSortedEquipList } from './equipListSort';
import './EquipUseStatus.css';
import '../contract/ContractPanel.css';
import { reportError } from '../../utils/notify';

type PanelView =
  | { type: 'overview' }
  | { type: 'create' }
  | { type: 'detail'; equipmentId: number }
  | { type: 'edit'; equipmentId: number };

const OVERVIEW_VIEW: PanelView = { type: 'overview' };

interface Props {
  equipmentType?: EquipmentType;
}

function ManufacturingEquipPanel({
  equipmentType = 'MANUFACTURING',
}: Props) {
  const isInspection = equipmentType === 'INSPECTION';
  const title = isInspection ? '검사설비' : '제조설비';
  const companyId = useCompanyStore((state) => state.companyId);
  const [view, setView] = useState<PanelView>(OVERVIEW_VIEW);
  const [items, setItems] = useState<ManufacturingEquipment[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { sortKey, sortDir, toggleSort, sortMark, thClass } = useEquipListSort(
    'equipmentCode',
    'desc'
  );
  const sortedItems = useSortedEquipList(items, sortKey, sortDir);

  const loadItems = useCallback(
    async (keyword?: string) => {
      try {
        setLoading(true);
        const list = await fetchManufacturingEquipments(keyword, equipmentType);
        setItems(list);
        setError('');
      } catch {
        reportError(setError, `${title} 목록을 불러오지 못했습니다.`);
      } finally {
        setLoading(false);
      }
    },
    [equipmentType, title]
  );

  useEffect(() => {
    setView(OVERVIEW_VIEW);
    setSelectedId(null);
    setSearchKeyword('');
    void loadItems();
  }, [companyId, loadItems, setView]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadItems(searchKeyword);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, loadItems]);

  const openEquipment = (equipmentId: number) => {
    setSelectedId(equipmentId);
    setView({ type: 'detail', equipmentId });
  };

  const goOverview = () => {
    setSelectedId(null);
    setView(OVERVIEW_VIEW);
    void loadItems(searchKeyword);
  };

  return (
    <main className="contract-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>{title}</strong>
          <span>설비 선택 시 메인 화면 전환</span>
        </div>

        <div className="contract-panel__search">
          <input
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="설비명 / 코드 / 위치 검색"
          />
        </div>

        <div className="contract-panel__list-wrap">
          {loading ? (
            <p className="contract-panel__hint">불러오는 중...</p>
          ) : (
            <ul className="contract-panel__list">
              <li>
                <button
                  type="button"
                  className={`contract-panel__list-item${
                    view.type === 'overview' && selectedId == null ? ' is-active' : ''
                  }`}
                  onClick={goOverview}
                >
                  전체 보기 ({items.length})
                </button>
              </li>
              {sortedItems.length === 0 ? (
                <li>
                  <p className="contract-panel__hint">등록된 설비가 없습니다.</p>
                </li>
              ) : (
                sortedItems.map((item) => (
                  <li key={item.equipmentId}>
                    <button
                      type="button"
                      className={`contract-panel__list-item${
                        selectedId === item.equipmentId ? ' is-active' : ''
                      }`}
                      onClick={() => openEquipment(item.equipmentId)}
                    >
                      <span className="contract-panel__list-name">{item.equipmentName}</span>
                      <span className="contract-panel__list-meta">
                        {item.equipmentCode ? `${item.equipmentCode} · ` : ''}
                        {item.ownershipType === 'RENTAL' ? '대여 · ' : ''}
                        {item.manufacturer || item.location || '제원 미입력'}
                        {' · '}
                        <EquipUseStatus useYn={item.useYn} />
                        {isInspection && item.calibStatus === 'EXPIRED' && (
                          <span className="equip-calib-status is-expired" style={{ marginLeft: 4 }}>
                            교정만료
                          </span>
                        )}
                        {isInspection && item.calibStatus === 'EXPIRING' && (
                          <span className="equip-calib-status is-expiring" style={{ marginLeft: 4 }}>
                            만료임박
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </aside>

      <section className="contract-panel__main">
        {view.type === 'create' ? (
          <ManufacturingEquipForm
            mode="create"
            equipmentType={equipmentType}
            onCancel={goOverview}
            onSaved={(saved) => {
              void loadItems(searchKeyword);
              openEquipment(saved.equipmentId);
            }}
          />
        ) : view.type === 'edit' ? (
          <ManufacturingEquipForm
            mode="edit"
            equipmentType={equipmentType}
            equipmentId={view.equipmentId}
            onCancel={() => openEquipment(view.equipmentId)}
            onSaved={(saved) => {
              void loadItems(searchKeyword);
              openEquipment(saved.equipmentId);
            }}
          />
        ) : view.type === 'detail' ? (
          <ManufacturingEquipDetail
            equipmentId={view.equipmentId}
            equipmentType={equipmentType}
            onBack={goOverview}
            onEdit={() => setView({ type: 'edit', equipmentId: view.equipmentId })}
            onStatusChanged={() => {
              void loadItems(searchKeyword);
            }}
            onDeleted={goOverview}
          />
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>{isInspection ? '검사설비 교정 현황' : `${title} 현황`}</h2>
                <p>
                  {isInspection
                    ? '전체 설비의 교정 만료·임박 상태를 한눈에 보고, 행을 클릭하면 상세로 이동합니다.'
                    : '왼쪽에서 설비를 선택하면 가운데 화면이 바뀝니다.'}
                </p>
              </div>
              <button
                type="button"
                className="contract-panel__primary"
                onClick={() => setView({ type: 'create' })}
              >
                설비 등록
              </button>
            </div>

            {error && <p className="contract-panel__error">{error}</p>}

            <div className="contract-panel__content">
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : items.length === 0 ? (
                <p className="contract-panel__hint">설비를 등록해 주세요.</p>
              ) : isInspection ? (
                <InspectionCalibOverview
                  items={items}
                  selectedId={selectedId}
                  onSelect={openEquipment}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onToggleSort={toggleSort}
                  sortMark={sortMark}
                  thClass={thClass}
                />
              ) : (
                <table className="ec-table contract-panel__table">
                  <thead>
                    <tr>
                      <th
                        style={{ width: '64px' }}
                        className={thClass('sortOrder')}
                        onClick={() => toggleSort('sortOrder')}
                      >
                        순서{sortMark('sortOrder')}
                      </th>
                      <th
                        style={{ width: '120px' }}
                        className={thClass('equipmentCode')}
                        onClick={() => toggleSort('equipmentCode')}
                      >
                        설비번호{sortMark('equipmentCode')}
                      </th>
                      <th>설비명</th>
                      <th style={{ width: '140px' }}>제작사</th>
                      <th style={{ width: '110px' }}>설치일</th>
                      <th style={{ width: '90px' }}>담당자</th>
                      <th style={{ width: '90px' }}>사용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((item) => (
                      <tr
                        key={item.equipmentId}
                        className={selectedId === item.equipmentId ? 'is-selected' : ''}
                        onClick={() => openEquipment(item.equipmentId)}
                      >
                        <td>{(item.sortOrder ?? 0) + 1}</td>
                        <td>{item.equipmentCode || '-'}</td>
                        <td>{item.equipmentName}</td>
                        <td>{item.manufacturer || '-'}</td>
                        <td>{item.installDate || '-'}</td>
                        <td>{item.managerName || '-'}</td>
                        <td>
                          <EquipUseStatus useYn={item.useYn} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default ManufacturingEquipPanel;
